import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { DEPARTMENTS } from "@/lib/departments";
import { compressImage } from "@/lib/image";
import { toast } from "sonner";
import { Camera } from "@phosphor-icons/react";

export default function DoudouNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", description: "", department: "", photo: null, secret_detail: "" });
  const [loading, setLoading] = useState(false);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Photo trop lourde (max 15 Mo)"); return; }
    try {
      const compressed = await compressImage(file);
      setForm({ ...form, photo: compressed });
    } catch {
      toast.error("Impossible de lire cette photo");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/doudous", form);
      toast.success("Fiche créée !");
      navigate(`/doudou/${data.doudou_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="font-display text-3xl font-black text-stone-800" data-testid="new-doudou-title">Nouvelle fiche doudou</h1>
        <p className="text-stone-600 mt-2">Remplissez les informations principales pour ce doudou.</p>

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-rose-100 p-8 space-y-5 shadow-sm">
          <div>
            <Label>Photo (facultatif)</Label>
            <label className="mt-2 flex flex-col items-center justify-center w-full min-h-[12rem] rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/50 cursor-pointer hover:bg-rose-50 transition overflow-hidden" data-testid="doudou-photo-upload">
              {form.photo ? (
                <img src={form.photo} alt="aperçu" className="w-full max-h-[28rem] object-contain rounded-2xl" />
              ) : (
                <>
                  <Camera size={32} weight="duotone" className="text-rose-400" />
                  <span className="text-sm text-stone-500 mt-2">Cliquez pour ajouter une photo</span>
                </>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            </label>
          </div>
          <div>
            <Label htmlFor="name">Prénom du doudou *</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Câline, Nounours, Léo..." className="rounded-2xl mt-1 py-6" data-testid="doudou-name-input" />
          </div>
          <div>
            <Label htmlFor="desc">Description</Label>
            <Textarea id="desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Lapin rose, oreille gauche un peu décousue, écharpe rayée..." className="rounded-2xl mt-1" data-testid="doudou-description-input" />
          </div>
          <div>
            <Label htmlFor="dept">Département</Label>
            <select id="dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full mt-1 rounded-2xl border-2 border-rose-100 px-4 py-3 bg-white" data-testid="doudou-department-select">
              <option value="">Sélectionner...</option>
              <optgroup label="Métropole">
                {DEPARTMENTS.filter(d => !d.code.startsWith("9") || d.code.length === 2).map((d) => (
                  <option key={d.code} value={d.code}>{d.code} — {d.name}</option>
                ))}
              </optgroup>
              <optgroup label="Outre-mer">
                {DEPARTMENTS.filter(d => d.code.length === 3).map((d) => (
                  <option key={d.code} value={d.code}>{d.code} — {d.name}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <Label htmlFor="secret">Détail privé pour vérifier qu'un trouveur a bien votre doudou <span className="text-stone-400 font-normal">(facultatif)</span></Label>
            <Textarea
              id="secret"
              rows={2}
              value={form.secret_detail}
              onChange={(e) => setForm({ ...form, secret_detail: e.target.value })}
              placeholder="Ex : petite tache rouge sous l'oreille gauche, étiquette « Léa » à l'intérieur..."
              className="rounded-2xl mt-1"
              data-testid="doudou-secret-input"
            />
            <p className="text-xs text-stone-500 mt-1">
              🔒 Visible uniquement par vous. Non obligatoire : vous pourrez le glisser dans une conversation pour vérifier que c'est bien votre doudou — ce n'est jamais bloquant.
            </p>
          </div>
          <Button type="submit" disabled={loading || !form.name} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold shadow-lg shadow-orange-500/30" data-testid="doudou-submit-button">
            {loading ? "Création..." : "Créer la fiche"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
